// Options-side trading limits by permission tier. Pure (no storage/app imports)
// so it can be unit-tested directly. This file is copied verbatim into
// Trade_Subscriber_Serverless_API (utils/optionsLimits.js) and
// Tradovate_API_Subscriber_UI (src/utils/optionsLimits.js) — keep all three identical.
//
// A user's tier and account limit live in their Dynamo users `attributes`:
//   optionsTier:        'basic' | 'premium' | 'unlimited' | 'legacy' (missing = legacy)
//   maxOptionsAccounts: number (missing = the tier's default)
// Admins set both from the Admin tab. New signups are onboarded as basic / 1.

export const OPTIONS_BROKERAGES = ['schwab', 'alpaca'];

export const OPTIONS_TIER_BASIC = 'basic';
export const OPTIONS_TIER_PREMIUM = 'premium';
export const OPTIONS_TIER_UNLIMITED = 'unlimited';
// Users who predate tiers: today's rules (3 accounts, Last Call max 5).
export const OPTIONS_TIER_LEGACY = 'legacy';

// Per-algo caps are contracts per account per entry signal, in SPX units
// (10 XSP = 1 SPX). An algo missing from `caps` is uncapped. Keys are the
// lowercase algo base name (the part of "MarketPower_SPX_1DTE" before "_").
export const OPTIONS_TIERS = {
    [OPTIONS_TIER_BASIC]: {
        label: 'Basic',
        defaultMaxAccounts: 1,
        caps: { marketpower: 2, doubledip: 5, phoenix: 1, goodmorning: 1, lastcall: 2 },
    },
    [OPTIONS_TIER_PREMIUM]: {
        label: 'Premium',
        defaultMaxAccounts: 1,
        caps: { marketpower: 4, doubledip: 10, phoenix: 2, goodmorning: 2, lastcall: 4 },
    },
    [OPTIONS_TIER_UNLIMITED]: {
        label: 'Unlimited',
        defaultMaxAccounts: 1,
        caps: {},
    },
    [OPTIONS_TIER_LEGACY]: {
        label: 'Legacy',
        defaultMaxAccounts: 3,
        caps: { lastcall: 5 },
    },
};

export const OPTIONS_TIER_NAMES = Object.keys(OPTIONS_TIERS);

const ALGO_LABELS = {
    marketpower: 'Market Power',
    doubledip: 'Double Dip',
    phoenix: 'Phoenix',
    goodmorning: 'Good Morning',
    lastcall: 'Last Call',
};

// "MarketPower_SPX_1DTE" or "MarketPower" → "marketpower"
export const algoBase = (savedAlgoOrAlgoName) =>
    String(savedAlgoOrAlgoName ?? '').split('_')[0].toLowerCase();

export const getAlgoLabel = (algo) => ALGO_LABELS[algoBase(algo)] || String(algo);

export const getOptionsTier = (userAccess) =>
    OPTIONS_TIERS[userAccess?.optionsTier] ? userAccess.optionsTier : OPTIONS_TIER_LEGACY;

export const getOptionsTierLabel = (tier) => OPTIONS_TIERS[tier]?.label || tier;

export const MAX_OPTIONS_ACCOUNTS_LIMIT = 20;

// Validates admin-supplied options access. Both values are optional; when given,
// the tier must be known and the account limit a whole number from 1 to 20.
// Returns an error message, or null when valid.
export const validateOptionsAccess = (optionsTier, maxOptionsAccounts) => {
    if (optionsTier !== undefined && optionsTier !== null && !OPTIONS_TIERS[optionsTier]) {
        return `Invalid optionsTier. Valid tiers: ${OPTIONS_TIER_NAMES.join(', ')}`;
    }
    if (maxOptionsAccounts !== undefined && maxOptionsAccounts !== null && maxOptionsAccounts !== '') {
        const n = Number(maxOptionsAccounts);
        if (!Number.isInteger(n) || n < 1 || n > MAX_OPTIONS_ACCOUNTS_LIMIT) {
            return `maxOptionsAccounts must be a whole number from 1 to ${MAX_OPTIONS_ACCOUNTS_LIMIT}`;
        }
    }
    return null;
};

export const getMaxOptionsAccounts = (userAccess) => {
    const explicit = Number(userAccess?.maxOptionsAccounts);
    if (Number.isInteger(explicit) && explicit >= 1) return explicit;
    return OPTIONS_TIERS[getOptionsTier(userAccess)].defaultMaxAccounts;
};

// Cap in SPX units for one algo under a tier, or null when uncapped.
export const getAlgoContractCap = (tier, algo) => {
    const cap = OPTIONS_TIERS[tier]?.caps?.[algoBase(algo)];
    return typeof cap === 'number' ? cap : null;
};

// XSP is 1/10 the notional of SPX.
export const toSpxUnits = (qty, ticker) => (ticker === 'XSP' ? qty / 10 : qty);

// Adjustments scale off the original entry and are exempt from tier caps.
export const isAdjustmentTrade = (trade) =>
    !!trade?.originalTradeKey || /-adjustment$/.test(String(trade?.entryStyle ?? ''));

// Distinct enabled account names across all options connections in a config.
// Only enableTrading accounts count; Tradovate connections are never counted.
export const countEnabledOptionsAccounts = (tradeConfig, credentials) => {
    const names = new Set();
    for (const buid in tradeConfig) {
        if (buid === 'undefined') continue;
        if (!OPTIONS_BROKERAGES.includes(credentials?.[buid]?.broker)) continue;

        const groups = tradeConfig[buid]?.accountGroups || [];
        for (const group of groups) {
            for (const account of group.accountsToTrade || []) {
                if (account.enableTrading && account.name) {
                    // Namespaced by connection: the same account name under two
                    // connections is two real accounts.
                    names.add(`${buid}|${account.name}`);
                }
            }
        }
    }
    return names.size;
};

// Per account and per capped algo, sum the static (# mode) contract counts of
// every group that trades that account and has the algo selected, in SPX units.
// A group whose only ticker for the algo is XSP counts at 1/10. $ and % groups
// are skipped here — the engine caps those at order placement.
// Returns [{ buid, account, algo, total, cap, groups }] for every sum over its cap.
export const findContractCapViolations = (tradeConfig, credentials, userAccess) => {
    const tier = getOptionsTier(userAccess);
    const violations = [];
    for (const buid in tradeConfig) {
        if (buid === 'undefined') continue;
        if (!OPTIONS_BROKERAGES.includes(credentials?.[buid]?.broker)) continue;

        const totals = {}; // account -> algo -> { total, groups }
        for (const group of tradeConfig[buid]?.accountGroups || []) {
            if (group.riskMode !== 'contracts') continue;
            const n = Math.floor(Number(group.numContracts));
            if (!(n >= 1)) continue;

            const tickersByAlgo = {};
            for (const savedAlgo of group.savedAlgos || []) {
                const algo = algoBase(savedAlgo);
                if (getAlgoContractCap(tier, algo) === null) continue;
                const ticker = String(savedAlgo).split('_')[1]?.toUpperCase();
                (tickersByAlgo[algo] ||= new Set()).add(ticker);
            }

            for (const algo in tickersByAlgo) {
                const xspOnly = tickersByAlgo[algo].size === 1 && tickersByAlgo[algo].has('XSP');
                const units = toSpxUnits(n, xspOnly ? 'XSP' : 'SPX');
                for (const account of group.accountsToTrade || []) {
                    if (!account.enableTrading || !account.name) continue;
                    const entry = ((totals[account.name] ||= {})[algo] ||= { total: 0, groups: [] });
                    entry.total += units;
                    entry.groups.push(group.groupName);
                }
            }
        }

        for (const account in totals) {
            for (const algo in totals[account]) {
                const { total, groups } = totals[account][algo];
                const cap = getAlgoContractCap(tier, algo);
                if (total > cap) violations.push({ buid, account, algo, total, cap, groups });
            }
        }
    }
    return violations;
};

export const formatContractCapViolation = (violation, userAccess) => {
    const { account, algo, total, cap, groups } = violation;
    const tierLabel = getOptionsTierLabel(getOptionsTier(userAccess));
    const across = groups.length > 1 ? ` across groups ${groups.join(', ')}` : ` in group ${groups[0]}`;
    return `${tierLabel} allows ${cap} ${getAlgoLabel(algo)} contract${cap === 1 ? '' : 's'} per account per trade; account ${account} has ${total}${across}.`;
};
